import { memo, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { UfSelect } from "@/shared/components/UfSelect";
import { Spinner } from "@/shared/components/ui/spinner";
import {
  formatCEP,
  formatDocument,
  formatPhone,
} from "@/shared/lib/formatters";
import { PersonType } from "@/shared/types/enums";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import type { ManualInvoiceRequestDTO } from "../types/invoice.types";
import { InvoiceClientSearch } from "./InvoiceClientSearch";
import {
  CNPJ_LENGTH,
  CPF_LENGTH,
  cleanDocument,
  normalizeDocumentInput,
} from "@/shared/lib/document";

const SELECT_CLASSES =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm";

/** Formata um Date como valor de <input type="date"> (yyyy-MM-dd) em horário local. */
const toDateInputValue = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const schema = z
  .object({
    clientId: z.number().nullable(),
    personType: z.nativeEnum(PersonType),
    name: z.string(),
    document: z.string(),
    gender: z.enum(["M", "F"]).or(z.literal("")).optional(),
    phone: z.string(),
    email: z.string().email("E-mail inválido").optional().or(z.literal("")),
    street: z.string(),
    number: z.string().max(10, "Número deve ter no máximo 10 caracteres"),
    complement: z.string(),
    neighborhood: z.string(),
    state: z.string(),
    postalCode: z.string(),
    value: z
      .number({ error: "Valor obrigatório" })
      .positive("Valor deve ser positivo"),
    paymentMethod: z.enum(["PIX", "CREDIT_CARD", "DEBIT_CARD", "CASH"]),
    invoiceObservation: z
      .string()
      .max(2000, "Máximo 2000 caracteres")
      .optional(),
    // Data de emissão (yyyy-MM-dd). Vazio = backend usa a data/hora atual.
    emissionDate: z
      .string()
      .optional()
      .refine(
        (v) => !v || v <= toDateInputValue(new Date()),
        "Data de emissão não pode ser futura",
      ),
  })
  .superRefine((data, ctx) => {
    // Cliente novo (sem clientId): documento + endereço completo obrigatórios
    // para a Focus NFe aceitar a nota.
    if (data.clientId) return;
    const isJuridica = data.personType === PersonType.JURIDICA;
    const required: Array<[keyof typeof data, string, number]> = [
      ["name", "Nome obrigatório", 1],
      ["phone", "Telefone obrigatório", 10],
      ["street", "Rua obrigatória", 1],
      ["number", "Número obrigatório", 1],
      ["neighborhood", "Bairro obrigatório", 1],
      ["postalCode", "CEP obrigatório", 8],
    ];
    for (const [field, message, min] of required) {
      const val = data[field];
      if (typeof val !== "string" || val.length < min) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
      }
    }
    const document = cleanDocument(data.document);
    if (
      isJuridica
        ? document.length !== CNPJ_LENGTH
        : document.length !== CPF_LENGTH
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["document"],
        message: isJuridica ? "CNPJ inválido" : "CPF inválido",
      });
    }
    if (data.state.length !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["state"],
        message: "UF deve ter 2 letras",
      });
    }
  });

type FormData = z.infer<typeof schema>;

type ClientMode = "search" | "selected" | "new";

interface ManualInvoiceDialogProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onSubmit: (data: ManualInvoiceRequestDTO) => void;
}

/** Hora atual (HH:mm:ss) no fuso de São Paulo — offset fixo -03:00, sem DST desde 2019. */
const currentTimeInSaoPaulo = (): string => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Sao_Paulo",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${hour}:${get("minute")}:${get("second")}`;
};

/**
 * Converte a data escolhida (yyyy-MM-dd) num ISO 8601 fixado no fuso de São
 * Paulo (-03:00) com o horário atual de SP. Fixar o offset torna a data civil
 * enviada à Focus NFe independente do fuso do navegador; usar o horário atual
 * (e não meia-noite/meio-dia) mantém a emissão do mesmo dia sempre <= agora.
 * Vazio/inválido → undefined (backend usa a data/hora atual).
 */
const buildEmissionIso = (dateStr: string | undefined): string | undefined => {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return undefined;
  return `${dateStr}T${currentTimeInSaoPaulo()}-03:00`;
};

const DEFAULT_VALUES: FormData = {
  clientId: null,
  personType: PersonType.FISICA,
  name: "",
  document: "",
  gender: "M",
  phone: "",
  email: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  state: "DF",
  postalCode: "",
  value: 0,
  paymentMethod: "PIX",
  invoiceObservation: "",
  emissionDate: toDateInputValue(new Date()),
};

/**
 * Dialog de emissão manual de NF avulsa (ADMIN).
 *
 * O tomador pode ser um cliente já cadastrado (busca → `clientId`) ou um
 * cliente novo, cujos dados (com endereço completo) são enviados inline no
 * payload — o backend reusa pelo documento ou cria. Os campos do cliente
 * novo só são validados quando não há `clientId`.
 */
export const ManualInvoiceDialog = memo(function ManualInvoiceDialog({
  open,
  loading,
  onClose,
  onSubmit,
}: ManualInvoiceDialogProps) {
  const [clientMode, setClientMode] = useState<ClientMode>("search");
  const [selectedClient, setSelectedClient] =
    useState<ClientResponseDTO | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  const isJuridica = form.watch("personType") === PersonType.JURIDICA;

  // A cada abertura, fixa a data de emissão em hoje (evita herdar a data de
  // uma emissão anterior ou de antes da virada do dia).
  useEffect(() => {
    if (open) {
      form.setValue("emissionDate", toDateInputValue(new Date()));
    }
  }, [open, form]);

  const handleSelectExisting = (client: ClientResponseDTO) => {
    setSelectedClient(client);
    form.setValue("clientId", client.id);
    setClientMode("selected");
  };

  const handleCreateNew = () => {
    setSelectedClient(null);
    form.setValue("clientId", null);
    setClientMode("new");
  };

  const handleResetClient = () => {
    setSelectedClient(null);
    form.setValue("clientId", null);
    setClientMode("search");
  };

  const handleSubmit = (data: FormData) => {
    if (!data.clientId && clientMode !== "new") {
      form.setError("clientId", {
        message: "Selecione um cliente ou cadastre um novo",
      });
      return;
    }

    const client = data.clientId
      ? { clientId: data.clientId }
      : {
          name: data.name,
          document: data.document,
          personType: data.personType,
          gender:
            data.personType === PersonType.JURIDICA
              ? undefined
              : (data.gender as "M" | "F"),
          phone: data.phone,
          email: data.email || undefined,
          street: data.street,
          number: data.number,
          complement: data.complement || undefined,
          neighborhood: data.neighborhood,
          state: data.state,
          postalCode: data.postalCode,
        };

    const invoiceObservation = data.invoiceObservation?.trim();
    const emissionDate = buildEmissionIso(data.emissionDate);

    onSubmit({
      client,
      value: data.value,
      paymentMethod: data.paymentMethod,
      ...(invoiceObservation ? { invoiceObservation } : {}),
      ...(emissionDate ? { emissionDate } : {}),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Emitir NF manual</DialogTitle>
          <DialogDescription>
            Emite uma nota fiscal avulsa em nome da loja. A autorização final
            chega de forma assíncrona — o status inicial costuma ser
            "Aguardando".
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {/* Tomador / cliente */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Cliente *</Label>
                {clientMode === "search" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCreateNew}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Cadastrar novo
                  </Button>
                )}
              </div>

              {clientMode === "search" && (
                <InvoiceClientSearch onSelect={handleSelectExisting} />
              )}

              {clientMode === "selected" && selectedClient && (
                <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {selectedClient.name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {formatDocument(selectedClient.document)} ·{" "}
                      {formatPhone(selectedClient.phone)}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleResetClient}
                  >
                    <X className="w-4 h-4" />
                    <span className="ml-1">Trocar</span>
                  </Button>
                </div>
              )}

              {form.formState.errors.clientId && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.clientId.message}
                </p>
              )}
            </div>

            {/* Cadastro de cliente novo */}
            {clientMode === "new" && (
              <div className="space-y-4 rounded-md border border-dashed border-input p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Dados do novo cliente</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleResetClient}
                  >
                    <X className="w-4 h-4" />
                    <span className="ml-1">Voltar à busca</span>
                  </Button>
                </div>

                <FormField
                  control={form.control}
                  name="personType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de pessoa *</FormLabel>
                      <FormControl>
                        <select
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value as PersonType;
                            field.onChange(value);
                            if (value === PersonType.JURIDICA) {
                              form.setValue("gender", "");
                            } else if (!form.getValues("gender")) {
                              form.setValue("gender", "M");
                            }
                          }}
                          className={`${SELECT_CLASSES} md:max-w-xs`}
                        >
                          <option value={PersonType.FISICA}>
                            Pessoa Física (CPF)
                          </option>
                          <option value={PersonType.JURIDICA}>
                            Pessoa Jurídica (CNPJ)
                          </option>
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {isJuridica ? "Razão Social *" : "Nome *"}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={
                              isJuridica
                                ? "Manicorp Trajes LTDA"
                                : "João da Silva"
                            }
                            maxLength={150}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="document"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{isJuridica ? "CNPJ *" : "CPF *"}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={
                              isJuridica
                                ? "11.222.333/0001-81"
                                : "000.000.000-00"
                            }
                            value={
                              field.value ? formatDocument(field.value) : ""
                            }
                            onChange={(e) =>
                              field.onChange(
                                normalizeDocumentInput(
                                  e.target.value,
                                  isJuridica,
                                ),
                              )
                            }
                            maxLength={18}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {!isJuridica && (
                    <FormField
                      control={form.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Gênero *</FormLabel>
                          <FormControl>
                            <select
                              value={field.value ?? ""}
                              onChange={field.onChange}
                              className={SELECT_CLASSES}
                            >
                              <option value="M">Masculino</option>
                              <option value="F">Feminino</option>
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telefone *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="(11) 99999-9999"
                            value={field.value ? formatPhone(field.value) : ""}
                            onChange={(e) =>
                              field.onChange(e.target.value.replace(/\D/g, ""))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>E-mail</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="email@exemplo.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CEP *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="00000-000"
                            value={field.value ? formatCEP(field.value) : ""}
                            onChange={(e) =>
                              field.onChange(e.target.value.replace(/\D/g, ""))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>UF *</FormLabel>
                        <FormControl>
                          <UfSelect
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="street"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rua *</FormLabel>
                        <FormControl>
                          <Input placeholder="Av. Paulista" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="number"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Número *</FormLabel>
                        <FormControl>
                          <Input placeholder="123" maxLength={10} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="complement"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Complemento</FormLabel>
                        <FormControl>
                          <Input placeholder="Apto 101" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="neighborhood"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bairro *</FormLabel>
                        <FormControl>
                          <Input placeholder="Bela Vista" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Dados da nota */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor (R$) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0,00"
                        value={Number.isNaN(field.value) ? "" : field.value}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? Number.NaN
                              : Number(e.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Forma de pagamento *</FormLabel>
                    <FormControl>
                      <select
                        value={field.value}
                        onChange={field.onChange}
                        className={SELECT_CLASSES}
                      >
                        <option value="PIX">PIX</option>
                        <option value="CREDIT_CARD">Crédito</option>
                        <option value="DEBIT_CARD">Débito</option>
                        <option value="CASH">Dinheiro</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emissionDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de emissão</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        max={toDateInputValue(new Date())}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Padrão: hoje. Em branco, usa a data atual.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="invoiceObservation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observação da NF</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descrição que aparece na nota fiscal"
                      rows={3}
                      maxLength={2000}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Opcional. Em branco usa o padrão da loja.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="gap-2">
                {loading && <Spinner className="w-4 h-4" />}
                Emitir NF
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
